import {
  formatDateLabel,
  opaqueRegistrationId,
  sessionKey,
  type EventAttendanceSession,
  type EventSessionRow,
  type LegacyAttendanceRow,
  type LiftEvent,
  type Registration,
  type RegistrationStatus,
} from "./data";

export type RegisterReason =
  | "registered"
  | "waitlisted"
  | "already_registered"
  | "already_waitlisted"
  | "full"
  | "event_closed";

export type RegisterResult = {
  registration: Registration | null;
  reason: RegisterReason;
  created: boolean;
};

export type CheckInResult =
  | "checked_in"
  | "already"
  | "not_registered"
  | "waitlisted"
  | "cancelled"
  | "invalid"
  | "wrong_event"
  | "event_closed";

export type EventCounts = {
  registered: number;
  waitlisted: number;
  checkedIn: number;
  remaining: number;
  cancelled: number;
};

export type ParsedCheckIn =
  | { kind: "registration"; registrationId: string }
  | { kind: "legacy"; eventId: string; studentId: string };

export function newRegistrationId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `r${crypto.randomUUID().replace(/-/g, "").slice(0, 12)}`;
  }
  return `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

export function checkInCode(registrationId: string): string {
  return `LIFTREG/${registrationId}`;
}

export function parseCheckInCode(raw: string): ParsedCheckIn | null {
  const code = raw.trim();
  if (!code) return null;
  const registration = /^LIFTREG\/([A-Za-z0-9_-]+)$/i.exec(code);
  if (registration) {
    return { kind: "registration", registrationId: registration[1] };
  }
  const legacy = /^LIFT\/([^/]+)\/([^/]+)$/i.exec(code);
  if (legacy) {
    return { kind: "legacy", eventId: legacy[1], studentId: legacy[2] };
  }
  return null;
}

export function isActiveStatus(status: RegistrationStatus): boolean {
  return status === "registered" || status === "waitlisted";
}

export function activeRegistration(
  registrations: Registration[],
  studentId: string,
): Registration | undefined {
  return registrations.find(
    (row) => row.studentId === studentId && isActiveStatus(row.status),
  );
}

export function cancelledRegistration(
  registrations: Registration[],
  studentId: string,
): Registration | undefined {
  return registrations.find(
    (row) => row.studentId === studentId && row.status === "cancelled",
  );
}

export function registeredCount(registrations: Registration[]): number {
  return registrations.filter((row) => row.status === "registered").length;
}

export function eventCounts(
  event: LiftEvent,
  registrations: Registration[],
): EventCounts {
  const registered = registeredCount(registrations);
  return {
    registered,
    waitlisted: registrations.filter((row) => row.status === "waitlisted")
      .length,
    checkedIn: registrations.filter(
      (row) => row.status === "registered" && Boolean(row.checkedInAt),
    ).length,
    remaining: Math.max(0, event.capacity - registered),
    cancelled: registrations.filter((row) => row.status === "cancelled").length,
  };
}

export function withRegisteredCount(
  event: LiftEvent,
  registrations: Registration[],
): LiftEvent {
  return { ...event, registered: registeredCount(registrations) };
}

export function orderedWaitlist(registrations: Registration[]): Registration[] {
  return registrations
    .filter((row) => row.status === "waitlisted")
    .sort((a, b) =>
      (a.waitlistedAt ?? a.registeredAt).localeCompare(
        b.waitlistedAt ?? b.registeredAt,
      ),
    );
}

/** 1-based place in join order. Null when the student is not waiting. */
export function waitlistPlace(
  registrations: Registration[],
  studentId: string,
): number | null {
  const index = orderedWaitlist(registrations).findIndex(
    (row) => row.studentId === studentId,
  );
  return index < 0 ? null : index + 1;
}

function promoteWaitlisted(
  event: LiftEvent,
  registrations: Registration[],
): Registration[] {
  if (registeredCount(registrations) >= event.capacity) return registrations;
  const next = orderedWaitlist(registrations)[0];
  if (!next) return registrations;
  return registrations.map((row) =>
    row.id === next.id ? { ...row, status: "registered" as const } : row,
  );
}

function eventIsClosed(event: LiftEvent): boolean {
  return event.status === "archived";
}

export function registerStudent(
  event: LiftEvent,
  registrations: Registration[],
  studentId: string,
  nowIso: string,
  answers: Record<string, string> = {},
  occupied?: number,
): { registrations: Registration[]; result: RegisterResult } {
  if (eventIsClosed(event)) {
    return {
      registrations,
      result: { registration: null, reason: "event_closed", created: false },
    };
  }

  const existing = activeRegistration(registrations, studentId);
  if (existing) {
    return {
      registrations,
      result: {
        registration: existing,
        reason:
          existing.status === "waitlisted"
            ? "already_waitlisted"
            : "already_registered",
        created: false,
      },
    };
  }

  const spots = event.capacity - (occupied ?? registeredCount(registrations));
  let status: RegistrationStatus;
  if (spots > 0) {
    status = "registered";
  } else if (event.waitlistEnabled) {
    status = "waitlisted";
  } else {
    return {
      registrations,
      result: { registration: null, reason: "full", created: false },
    };
  }

  const previous = cancelledRegistration(registrations, studentId);
  const registration: Registration = previous
    ? {
        ...previous,
        status,
        registeredAt: nowIso,
        waitlistedAt: status === "waitlisted" ? nowIso : null,
        cancelledAt: null,
        checkedInAt: null,
        checkedInBy: null,
        answers: {},
      }
    : {
        id: newRegistrationId(),
        eventId: event.id,
        studentId,
        status,
        registeredAt: nowIso,
        waitlistedAt: status === "waitlisted" ? nowIso : null,
        cancelledAt: null,
        checkedInAt: null,
        checkedInBy: null,
        answers: {},
      };

  const withAnswers: Registration = { ...registration, answers };

  const next = previous
    ? registrations.map((row) => (row.id === previous.id ? withAnswers : row))
    : [...registrations, withAnswers];

  return {
    registrations: next,
    result: {
      registration: withAnswers,
      reason: status === "waitlisted" ? "waitlisted" : "registered",
      created: true,
    },
  };
}

export function cancelRegistration(
  event: LiftEvent,
  registrations: Registration[],
  studentId: string,
  nowIso: string,
): { registrations: Registration[]; cancelled: Registration | null } {
  const existing = activeRegistration(registrations, studentId);
  if (!existing) return { registrations, cancelled: null };

  let next = registrations.map((row) =>
    row.id === existing.id
      ? {
          ...row,
          status: "cancelled" as const,
          cancelledAt: nowIso,
          checkedInAt: null,
          checkedInBy: null,
        }
      : row,
  );
  next = promoteWaitlisted(event, next);
  const cancelled = next.find((row) => row.id === existing.id) ?? null;
  return { registrations: next, cancelled };
}

export function resolveCheckInTarget(
  event: LiftEvent,
  registrations: Registration[],
  code: string,
): { registration: Registration | null; result: CheckInResult } {
  const parsed = parseCheckInCode(code);
  if (!parsed) {
    return { registration: null, result: "invalid" };
  }

  let registration: Registration | undefined;
  if (parsed.kind === "registration") {
    registration = registrations.find((row) => row.id === parsed.registrationId);
    if (!registration) {
      return { registration: null, result: "invalid" };
    }
    if (registration.eventId !== event.id) {
      return { registration, result: "wrong_event" };
    }
  } else {
    if (parsed.eventId !== event.id) {
      return { registration: null, result: "wrong_event" };
    }
    registration = registrations.find(
      (row) => row.studentId === parsed.studentId,
    );
    if (!registration) {
      return { registration: null, result: "not_registered" };
    }
  }

  return { registration, result: "checked_in" };
}

export function checkInRegistration(
  event: LiftEvent,
  registrations: Registration[],
  registrationId: string,
  checkedInBy: string,
  checkedInAt: string,
): { registrations: Registration[]; result: CheckInResult } {
  if (eventIsClosed(event)) {
    return { registrations, result: "event_closed" };
  }
  const registration = registrations.find((row) => row.id === registrationId);
  if (!registration) return { registrations, result: "not_registered" };
  if (registration.eventId !== event.id) {
    return { registrations, result: "wrong_event" };
  }
  return applyCheckIn(registrations, registration, checkedInBy, checkedInAt);
}

export function checkInByCode(
  event: LiftEvent,
  registrations: Registration[],
  code: string,
  checkedInBy: string,
  checkedInAt: string,
): { registrations: Registration[]; result: CheckInResult } {
  if (eventIsClosed(event)) {
    return { registrations, result: "event_closed" };
  }
  const resolved = resolveCheckInTarget(event, registrations, code);
  if (!resolved.registration || resolved.result !== "checked_in") {
    return { registrations, result: resolved.result };
  }
  return applyCheckIn(
    registrations,
    resolved.registration,
    checkedInBy,
    checkedInAt,
  );
}

function applyCheckIn(
  registrations: Registration[],
  registration: Registration,
  checkedInBy: string,
  checkedInAt: string,
): { registrations: Registration[]; result: CheckInResult } {
  if (registration.status === "cancelled") {
    return { registrations, result: "cancelled" };
  }
  if (registration.status === "waitlisted") {
    return { registrations, result: "waitlisted" };
  }
  if (registration.checkedInAt) {
    return { registrations, result: "already" };
  }
  return {
    registrations: registrations.map((row) =>
      row.id === registration.id
        ? { ...row, checkedInAt, checkedInBy }
        : row,
    ),
    result: "checked_in",
  };
}

export function undoCheckIn(
  registrations: Registration[],
  registrationId: string,
): { registrations: Registration[]; undone: boolean } {
  const registration = registrations.find((row) => row.id === registrationId);
  if (!registration?.checkedInAt) {
    return { registrations, undone: false };
  }
  return {
    registrations: registrations.map((row) =>
      row.id === registrationId
        ? { ...row, checkedInAt: null, checkedInBy: null }
        : row,
    ),
    undone: true,
  };
}

export function migrateAttendanceToRegistrations(
  attendance: Record<string, LegacyAttendanceRow[]>,
): Record<string, Registration[]> {
  return Object.fromEntries(
    Object.entries(attendance).map(([eventId, rows]) => [
      eventId,
      rows.map((row) => ({
        id: opaqueRegistrationId(eventId, row.studentId),
        eventId,
        studentId: row.studentId,
        status: "registered" as const,
        registeredAt: "2026-09-01T16:00:00.000Z",
        waitlistedAt: null,
        cancelledAt: null,
        checkedInAt: row.status === "checked_in" ? row.at : null,
        checkedInBy: row.status === "checked_in" ? "audrey" : null,
        answers: {},
      })),
    ]),
  );
}

export function upsertAttendanceSession(
  sessions: Record<string, EventAttendanceSession>,
  eventId: string,
  dateIso: string,
  rows: EventSessionRow[],
  savedAt: string,
): { sessions: Record<string, EventAttendanceSession>; session: EventAttendanceSession } {
  const id = sessionKey(eventId, dateIso);
  const session: EventAttendanceSession = {
    id,
    eventId,
    dateIso,
    dateLabel: formatDateLabel(dateIso),
    savedAt,
    rows,
  };
  return { sessions: { ...sessions, [id]: session }, session };
}

export function rosterForAttendance(
  registrations: Registration[],
): Registration[] {
  return registrations.filter((row) => row.status === "registered");
}
